


// src/pages/Profile.jsx
import React, { useState, useEffect } from "react";
import ProfileService from "../../services/ProfileService";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Loader from "../../components/Loader/Loader";
import "./Profile.css";
import "./ProfileModal.css";

const Profile = () => {
  const [originalData, setOriginalData] = useState(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    userName: "",
    dob: "",
    gender: ""
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // 1) Load profile once
  useEffect(() => {
    setLoading(true);
    ProfileService.getProfile()
      .then((res) => {
        console.log('Profile data received:', res.data);
        // Format date to yyyy-MM-dd for HTML date input
        const formattedData = {
          ...res.data,
          dob: res.data.dob ? res.data.dob.split('T')[0] : ''
        };
        setUserData(formattedData);
        setOriginalData(formattedData);
      })
      .catch((error) => {
        console.error('Error loading profile:', error);
        toast.error("Failed to load profile");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // 2) Detect edits
  useEffect(() => {
    if (!originalData) return;
    const edited = Object.keys(userData).some(
      (key) => userData[key] !== originalData[key]
    );
    setHasChanges(edited);
  }, [userData, originalData]);

  const handleInputChange = ({ target: { name, value } }) => {
    setUserData((u) => ({ ...u, [name]: value }));
  };

  const handleSaveChanges = () => {
    ProfileService.updateProfile(userData)
      .then((res) => {
        console.log('Profile update response:', res.data);
        toast.success("Profile updated successfully");
        setOriginalData(userData);
      })
      .catch((error) => {
        console.error('Error updating profile:', error);
        // Show backend error message if present (uniqueness or other)
        const msg = error.response?.data?.message || "Failed to update profile";
        alert(msg); // Show alert for uniqueness or other errors
        toast.error(msg);
      });
  };

  const handlePasswordChangeInput = ({ target: { name, value } }) => {
    setPasswordData((p) => ({ ...p, [name]: value }));
  };

  const handleChangePassword = () => {
    ProfileService.changePassword(passwordData)
      .then((res) => {
        console.log('Password change response:', res.data);
        toast.success("Password changed successfully");
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      })
      .catch((err) => {
        console.error('Error changing password:', err);
        toast.error(err.response?.data?.message || "Failed to change password");
      });
  };

  if (loading) {
    return <Loader message="Loading profile..." />;
  }

  return (
    <div className="profile-container">
      <h1>My Profile</h1>

      <div className="profile-form">
        {[
          { field: "firstName", label: "First Name", type: "text", editable: true },
          { field: "lastName", label: "Last Name", type: "text", editable: true },
          { field: "userName", label: "Username", type: "text", editable: false },
          { field: "email", label: "Email", type: "email", editable: false },
          { field: "phone", label: "Phone", type: "tel", editable: true },
          { field: "address", label: "Address", type: "text", editable: true },
          { field: "dob", label: "Date of Birth", type: "date", editable: false },
          { field: "gender", label: "Gender", type: "text", editable: false }
        ].map(({ field, label, type, editable }) => (
          <div className="form-group" key={field}>
            <label>{label}</label>
            <input
              className="profile-input"
              type={type}
              name={field}
              value={userData[field] || ""}
              onChange={handleInputChange}
              readOnly={!editable}
              disabled={!editable}
            />
          </div>
        ))}
      </div>

      <div className="button-group" style={{ display: 'flex', gap: '1rem', marginTop: 32 }}>
        <button
          className="btn btn-secondary"
          disabled={!hasChanges}
          onClick={handleSaveChanges}
          style={{ flex: 1 }}
        >
          Save Changes
        </button>
        <button
          className="btn btn-primary"
          onClick={() => setShowPasswordModal(true)}
          style={{ flex: 1 }}
        >
          Change Password
        </button>
      </div>

      {showPasswordModal && (
        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} className="add-customer-modal">
          <Modal.Header closeButton>
            <Modal.Title className="title">Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form>
              <div className="form-group">
                <label>Old Password</label>
                <input
                  className="profile-input"
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChangeInput}
                  required
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  className="profile-input"
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChangeInput}
                  required
                />
                {passwordData.newPassword && (
                  <small className="text-muted">Password must be at least 6 characters long</small>
                )}
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  className="profile-input"
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChangeInput}
                  required
                />
                {passwordData.confirmPassword && (
                  <small className={passwordData.newPassword === passwordData.confirmPassword ? "text-success" : "text-danger"}>
                    {passwordData.newPassword === passwordData.confirmPassword ? "Passwords match" : "Passwords do not match"}
                  </small>
                )}
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-end w-100">
              <Button
                variant="primary"
                onClick={handleChangePassword}
                className="w-100 btn-primary"
                disabled={
                  !passwordData.currentPassword ||
                  !passwordData.newPassword ||
                  passwordData.newPassword.length < 6 ||
                  passwordData.newPassword !== passwordData.confirmPassword
                }
              >
                Change Password
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default Profile;
